/**
 * Small hex-color helpers for themable UI (envelope paper, seals, accents).
 * Every function is total: anything that is not a hex color comes back
 * unchanged, so a bad value from config can never break rendering.
 */

const HEX_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** True when the value is a `#RGB` / `#RRGGBB` color. */
export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX_PATTERN.test(value.trim());
}

/** Expand `#abc` to `#aabbcc`; returns the input for anything else. */
function toSixDigits(hex: string): string {
  const value = hex.trim();
  if (value.length !== 4) return value.toLowerCase();
  const [, r, g, b] = value;
  return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
}

/**
 * Shift a hex color toward black (negative percent) or white (positive),
 * e.g. `shadeHexColor('#f4efe6', -8)` for the shaded side of a paper fold.
 */
export function shadeHexColor(hex: string, percent: number): string {
  if (!isHexColor(hex)) return hex;
  const full = toSixDigits(hex);
  const amount = Math.max(-100, Math.min(100, percent)) / 100;
  const channel = (offset: number): string => {
    const value = parseInt(full.slice(offset, offset + 2), 16);
    const target = amount < 0 ? 0 : 255;
    const mixed = Math.round(value + (target - value) * Math.abs(amount));
    return Math.max(0, Math.min(255, mixed)).toString(16).padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}
